import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import DataTable, { TableColumn } from "../components/ui/DataTable";
import AddNewButton from "../components/ui/AddNewButton";
import Toggle from "../components/ui/Toggle";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import ConfirmModal from "../components/layout/ConfirmModal";
import UserForm, { UserFormValues } from "../components/users/UserForm";
import EmptyState from "../components/ui/EmptyState";
import TablePagination from "../components/ui/TablePagination";
import { useUsers, useUpdateUser, useDeleteUser, useResetUserPassword, User } from "../services/users";
import ROUTES from "../utils/ROUTES";
import { toast } from "../hooks/use-toast";
import StatusPill from "../components/ui/StatusPill";
import { Pencil, Trash2, KeyRound } from "lucide-react";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again.";

function getInitials(name: string): string {
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

const Users = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [resettingUser, setResettingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: usersData, isLoading, isError } = useUsers(currentPage);
  const users = usersData?.staff;
  const pagination = usersData?.pagination;
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();
  const { mutate: deleteUser } = useDeleteUser();
  const { mutate: resetPassword } = useResetUserPassword();

  const handleConfirmDelete = () => {
    if (!deletingUser) return;
    deleteUser(deletingUser.uid, { onSuccess: () => setDeletingUser(null) });
  };

  const handleConfirmReset = () => {
    if (!resettingUser) return;
    resetPassword(resettingUser.uid, {
      onSuccess: () => { toast({ variant: "success", title: "Password reset", description: `A reset link has been sent to ${resettingUser.email}.` }); setResettingUser(null); },
      onError: (err) => { toast({ variant: "error", title: "Failed to reset password", description: getErrorMessage(err) }); setResettingUser(null); },
    });
  };

  const handleSave = (values: UserFormValues) => {
    if (!editingUser) return;
    updateUser(
      { uid: editingUser.uid, display_name: values.display_name },
      {
        onSuccess: () => { toast({ variant: "success", title: "User updated", description: "The user has been successfully updated." }); setEditingUser(null); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update user", description: getErrorMessage(err) }); },
      }
    );
  };

  const handleToggle = (user: User, is_enabled: boolean) => {
    updateUser({ uid: user.uid, display_name: user.display_name, is_enabled });
  };

  const columns: TableColumn<User>[] = [
    {
      key: "display_name",
      header: "User Name",
      width: "30%",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[12px] font-medium text-content-heading"
            style={{ backgroundColor: "#cce7ef" }}
          >
            {getInitials(row.display_name)}
          </div>
          <span className="text-[14px] font-semibold text-content-heading">
            {row.display_name}
          </span>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email Address",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <span className="text-[14px] font-normal text-content-label">
          {row.email}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cellClassName: "px-6 py-[30px]",
      render: (row) => <StatusPill status={row.is_enabled ? "Active" : "InActive"} />,
    },
    {
      key: "actions",
      header: "Actions",
      headerAlign: "right",
      width: "180px",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <div className="relative group">
            <button
              onClick={() => setEditingUser(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Edit user"
            >
              <Pencil className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Edit user</span>
          </div>
          <div className="relative group">
            <button
              onClick={() => setDeletingUser(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Delete user"
            >
              <Trash2 className="w-[13px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Delete user</span>
          </div>
          <div className="relative group">
            <button
              onClick={() => setResettingUser(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Reset password"
            >
              <KeyRound className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Reset password</span>
          </div>
          <div className="pl-2 flex items-center">
            <Toggle
              enabled={row.is_enabled}
              onChange={(val) => handleToggle(row, val)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        <div className="flex items-center mb-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              Staff
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Manage registered users and their access status.
            </p>
          </div>

          <AddNewButton onClick={() => navigate(ROUTES.ADD_USER)} />
        </div>

        <div className="mt-8 lg:mt-[36px]">
          {isLoading && <p className="text-[14px] text-content-label">Loading…</p>}
          {isError && <p className="text-[14px] text-content-error">Failed to load users.</p>}
          {!isLoading && !isError && (
            !users?.length
              ? <EmptyState message="No users found." />
              : (
                  <>
                    <DataTable columns={columns} rows={users!} rowKey={(u) => u.uid} />
                    <TablePagination currentPage={currentPage} pagination={pagination} rowCount={users!.length} onPageChange={setCurrentPage} />
                  </>
                )
          )}
        </div>
      </main>

      <Modal
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
        title={`Edit "${editingUser?.display_name ?? ""}"`}
      >
        <UserForm
          initialValues={{ display_name: editingUser?.display_name, email: editingUser?.email }}
          isPending={isUpdating}
          disableEmail
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingUser !== null}
        onClose={() => setDeletingUser(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this user?"
        message={
          <>
            This action will permanently remove{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {deletingUser?.display_name}
            </span>{" "}
            from your system. This action cannot be undone.
          </>
        }
        confirmLabel="Delete User"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        isOpen={resettingUser !== null}
        onClose={() => setResettingUser(null)}
        onConfirm={handleConfirmReset}
        title="Reset this user's password?"
        message={
          <>
            A password reset link will be sent to{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {resettingUser?.email}
            </span>
            .
          </>
        }
        confirmLabel="Send Reset Link"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Users;
